import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Switch, 
  InputNumber, 
  DatePicker, 
  TimePicker,
  Row, 
  Col, 
  Typography, 
  Space, 
  Divider, 
  Alert, 
  Spin, 
  message,
  Tag,
  Tooltip,
  Popconfirm,
  Select
} from 'antd';
import { 
  SaveOutlined, 
  DeleteOutlined, 
  PlusOutlined, 
  MinusCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  MedicineBoxOutlined,
  EnvironmentOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_SCHEDULE, UPDATE_SCHEDULE, DELETE_SCHEDULE } from '../graphql/mutations';
import { GET_PROVIDER } from '../graphql/queries';
import { useTranslation } from '../hooks/useTranslation';
import { useGraphQLError } from '../hooks/useGraphQL';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface TimeSlot {
  from: string;
  to: string;
}

interface WorkingHours {
  monday?: TimeSlot[];
  tuesday?: TimeSlot[];
  wednesday?: TimeSlot[];
  thursday?: TimeSlot[];
  friday?: TimeSlot[];
  saturday?: TimeSlot[];
  sunday?: TimeSlot[];
}

interface ProviderSchedule {
  id: string;
  provider_id: string;
  status: string;
  from_date?: Date;
  to_date?: Date;
  schedule?: WorkingHours;
  timezone?: string;
  priority: number;
  default_appointment_duration?: number;
  max_appointments_per_day?: number;
  is_available_for_walk_in: boolean;
  is_available_for_telemedicine: boolean;
  advance_booking_days?: number;
  same_day_booking: boolean;
  min_notice_hours?: number;
  max_notice_days?: number;
  cancellation_hours?: number;
  reschedule_hours?: number;
  booking_window_start?: string;
  booking_window_end?: string;
  requires_approval: boolean;
  patient_type_restrictions?: string[];
  service_restrictions?: string[];
  insurance_restrictions?: string[];
  send_reminders: boolean;
  reminder_hours?: number[];
  confirmation_required: boolean;
  auto_confirm_hours?: number;
  created_by?: string;
  created_at: Date;
  updated_at: Date;
}

interface ProviderScheduleConfigProps {
  providerId: string;
  visible: boolean;
  onClose: () => void;
}

const ProviderScheduleConfig: React.FC<ProviderScheduleConfigProps> = ({ 
  providerId, 
  visible, 
  onClose 
}) => {
  const { t } = useTranslation();
  const { handleError } = useGraphQLError();
  const [form] = Form.useForm();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ProviderSchedule | null>(null);

  const { data, loading, refetch } = useQuery(GET_PROVIDER, {
    variables: { id: providerId },
    skip: !providerId
  });
  
  const [createSchedule] = useMutation(CREATE_SCHEDULE);
  const [updateSchedule] = useMutation(UPDATE_SCHEDULE);
  const [deleteSchedule] = useMutation(DELETE_SCHEDULE);

  const provider = data?.provider;
  const schedules = provider?.schedules || [];

  const weekDays = [
    { key: 'monday', label: t('schedule.monday'), icon: '1️⃣' },
    { key: 'tuesday', label: t('schedule.tuesday'), icon: '2️⃣' },
    { key: 'wednesday', label: t('schedule.wednesday'), icon: '3️⃣' },
    { key: 'thursday', label: t('schedule.thursday'), icon: '4️⃣' },
    { key: 'friday', label: t('schedule.friday'), icon: '5️⃣' },
    { key: 'saturday', label: t('schedule.saturday'), icon: '6️⃣' },
    { key: 'sunday', label: t('schedule.sunday'), icon: '7️⃣' }
  ];

  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
    '20:00', '20:30', '21:00'
  ];

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'success', text: t('common.active'), icon: <CheckCircleOutlined /> },
      inactive: { color: 'warning', text: t('common.inactive'), icon: <ExclamationCircleOutlined /> },
      draft: { color: 'default', text: t('common.draft'), icon: <EditOutlined /> },
      expired: { color: 'error', text: t('common.expired'), icon: <StopOutlined /> }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={config.color as any} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const handleCreateSchedule = async (values: any) => {
    try {
      setIsSubmitting(true);
      
      // Convert schedule format to match the example JSON structure
      const scheduleData = {
        ...values,
        schedule: convertScheduleFormat(values.schedule),
        from_date: values.from_date ? values.from_date.format('YYYY-MM-DD') : null,
        to_date: values.to_date ? values.to_date.format('YYYY-MM-DD') : null,
        booking_window_start: values.booking_window_start ? values.booking_window_start.format('HH:mm') : null,
        booking_window_end: values.booking_window_end ? values.booking_window_end.format('HH:mm') : null,
        reminder_hours: values.reminder_hours || [],
        patient_type_restrictions: values.patient_type_restrictions || [],
        service_restrictions: values.service_restrictions || [],
        insurance_restrictions: values.insurance_restrictions || []
      };

      await createSchedule({
        variables: {
          providerId,
          createScheduleDto: scheduleData
        }
      });

      message.success(t('schedule.scheduleCreated'));
      refetch();
      form.resetFields();
    } catch (error) {
      console.error('Error creating schedule:', error);
      message.error(handleError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSchedule = async (id: string, values: any) => {
    try {
      setIsSubmitting(true);
      
      const scheduleData = {
        ...values,
        schedule: convertScheduleFormat(values.schedule),
        from_date: values.from_date ? values.from_date.format('YYYY-MM-DD') : null,
        to_date: values.to_date ? values.to_date.format('YYYY-MM-DD') : null,
        booking_window_start: values.booking_window_start ? values.booking_window_start.format('HH:mm') : null,
        booking_window_end: values.booking_window_end ? values.booking_window_end.format('HH:mm') : null,
        reminder_hours: values.reminder_hours || [],
        patient_type_restrictions: values.patient_type_restrictions || [],
        service_restrictions: values.service_restrictions || [],
        insurance_restrictions: values.insurance_restrictions || []
      };

      await updateSchedule({
        variables: {
          id,
          updateScheduleDto: scheduleData
        }
      });

      message.success(t('schedule.scheduleUpdated'));
      refetch();
      setEditingSchedule(null);
      form.resetFields();
    } catch (error) {
      console.error('Error updating schedule:', error);
      message.error(handleError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    try {
      await deleteSchedule({
        variables: { id }
      });
      message.success(t('schedule.scheduleDeleted'));
      refetch();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      message.error(handleError(error));
    }
  };

  // Convert schedule format to match GraphQL WorkingHoursInput type (day names)
  const convertScheduleFormat = (schedule: WorkingHours) => {
    const converted: any = {};
    Object.entries(schedule).forEach(([day, slots]) => {
      if (slots && Array.isArray(slots) && slots.length > 0) {
        // Clean each time slot by removing __typename and other GraphQL metadata
        converted[day] = slots.map((slot: any) => ({
          from: slot.from,
          to: slot.to
        }));
      }
    });
    return converted;
  };

  // Convert from API format back to day names for editing
  const convertFromApiFormat = (schedule: any) => {
    // If schedule is already in day name format, return as is
    if (schedule && typeof schedule === 'object') {
      const dayMapping = {
        '1': 'monday',
        '2': 'tuesday',
        '3': 'wednesday', 
        '4': 'thursday',
        '5': 'friday',
        '6': 'saturday',
        '7': 'sunday'
      };

      // Check if it's using numeric keys (legacy format)
      const hasNumericKeys = Object.keys(schedule).some(key => dayMapping[key as keyof typeof dayMapping]);
      
      if (hasNumericKeys) {
        // Convert from numeric keys to day names
        const converted: WorkingHours = {};
        Object.entries(schedule).forEach(([day, slots]) => {
          if (dayMapping[day as keyof typeof dayMapping] && Array.isArray(slots)) {
            // Clean each time slot by removing __typename
            const cleanSlots = slots.map((slot: any) => ({
              from: slot.from,
              to: slot.to
            }));
            converted[dayMapping[day as keyof typeof dayMapping] as keyof WorkingHours] = cleanSlots;
          }
        });
        return converted;
      } else {
        // Already in day name format, clean the data
        const converted: WorkingHours = {};
        Object.entries(schedule).forEach(([day, slots]) => {
          if (slots && Array.isArray(slots)) {
            // Clean each time slot by removing __typename
            const cleanSlots = slots.map((slot: any) => ({
              from: slot.from,
              to: slot.to
            }));
            converted[day as keyof WorkingHours] = cleanSlots;
          }
        });
        return converted;
      }
    }
    return {};
  };

  const addTimeSlot = (dayKey: string) => {
    const currentSchedule = form.getFieldValue('schedule') || {};
    const daySchedule = currentSchedule[dayKey] || [];
    const newTimeSlot = { from: '09:00', to: '17:00' };
    
    form.setFieldsValue({
      schedule: {
        ...currentSchedule,
        [dayKey]: [...daySchedule, newTimeSlot]
      }
    });
  };

  const removeTimeSlot = (dayKey: string, index: number) => {
    const currentSchedule = form.getFieldValue('schedule') || {};
    const daySchedule = currentSchedule[dayKey] || [];
    daySchedule.splice(index, 1);
    
    form.setFieldsValue({
      schedule: {
        ...currentSchedule,
        [dayKey]: daySchedule
      }
    });
  };

  const updateTimeSlot = (dayKey: string, index: number, field: 'from' | 'to', value: string) => {
    const currentSchedule = form.getFieldValue('schedule') || {};
    const daySchedule = currentSchedule[dayKey] || [];
    daySchedule[index] = { ...daySchedule[index], [field]: value };
    
    form.setFieldsValue({
      schedule: {
        ...currentSchedule,
        [dayKey]: daySchedule
      }
    });
  };

  const editSchedule = (schedule: ProviderSchedule) => {
    setEditingSchedule(schedule);
    
    // Convert schedule format for editing
    const editableSchedule = schedule.schedule ? convertFromApiFormat(schedule.schedule) : {};
    
    form.setFieldsValue({
      ...schedule,
      from_date: schedule.from_date ? dayjs(schedule.from_date) : null,
      to_date: schedule.to_date ? dayjs(schedule.to_date) : null,
      booking_window_start: schedule.booking_window_start ? dayjs(schedule.booking_window_start, 'HH:mm') : null,
      booking_window_end: schedule.booking_window_end ? dayjs(schedule.booking_window_end, 'HH:mm') : null,
      schedule: editableSchedule
    });
  };

  const resetForm = () => {
    setEditingSchedule(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      priority: 0,
      default_appointment_duration: 30,
      max_appointments_per_day: 20,
      is_available_for_walk_in: false,
      is_available_for_telemedicine: true,
      advance_booking_days: 30,
      same_day_booking: true,
      min_notice_hours: 2,
      max_notice_days: 30,
      cancellation_hours: 24,
      reschedule_hours: 24,
      timezone: 'Asia/Hong_Kong',
      requires_approval: false,
      send_reminders: true,
      confirmation_required: false,
      schedule: {
        monday: [{ from: '09:00', to: '17:00' }],
        tuesday: [{ from: '09:00', to: '17:00' }],
        wednesday: [{ from: '09:00', to: '17:00' }],
        thursday: [{ from: '09:00', to: '17:00' }],
        friday: [{ from: '09:00', to: '17:00' }],
        saturday: [],
        sunday: []
      }
    });
  };

  useEffect(() => {
    if (visible && !editingSchedule) {
      resetForm();
    }
  }, [visible, editingSchedule]);

  if (loading) {
    return (
      <Modal
        title={t('schedule.providerScheduleConfig')}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={1200}
      >
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>{t('common.loading')}</div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ClockCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {t('schedule.providerScheduleConfig')}
          {provider && (
            <Text type="secondary" style={{ marginLeft: 8 }}>
              - {provider.doctor.first_name_en} {provider.doctor.last_name_en}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <Row gutter={24}>
        {/* Schedule List */}
        <Col xs={24} lg={8}>
          <Card title={t('schedule.existingSchedules')} size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {schedules.length === 0 ? (
                <Text type="secondary">{t('schedule.noSchedules')}</Text>
              ) : (
                schedules.map((schedule: ProviderSchedule) => (
                  <Card
                    key={schedule.id}
                    size="small"
                    hoverable
                    style={{ cursor: 'pointer' }}
                    onClick={() => editSchedule(schedule)}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>Schedule #{schedule.id.slice(-6)}</Text>
                        {getStatusTag(schedule.status)}
                      </div>
                      {schedule.from_date && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {dayjs(schedule.from_date).format('MMM DD')} - {schedule.to_date ? dayjs(schedule.to_date).format('MMM DD, YYYY') : 'Ongoing'}
                        </Text>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            editSchedule(schedule);
                          }}
                        >
                          {t('common.edit')}
                        </Button>
                        <Popconfirm
                          title={t('schedule.deleteConfirm')}
                          onConfirm={(e) => {
                            e?.stopPropagation();
                            handleDeleteSchedule(schedule.id);
                          }}
                          okText={t('common.yes')}
                          cancelText={t('common.no')}
                        >
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t('common.delete')}
                          </Button>
                        </Popconfirm>
                      </div>
                    </Space>
                  </Card>
                ))
              )}
              
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={resetForm}
                style={{ width: '100%' }}
              >
                {t('schedule.createNewSchedule')}
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Schedule Form */}
        <Col xs={24} lg={16}>
          <Card title={editingSchedule ? t('schedule.editSchedule') : t('schedule.createNewSchedule')}>
            <Form
              form={form}
              layout="vertical"
              onFinish={(values) => {
                if (editingSchedule) {
                  handleUpdateSchedule(editingSchedule.id, values);
                } else {
                  handleCreateSchedule(values);
                }
              }}
            >
              <Row gutter={24}>
                {/* General Settings */}
                <Col xs={24} lg={8}>
                  <Card size="small" title={t('schedule.general')} style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Form.Item
                        name="status"
                        label={t('common.status')}
                        rules={[{ required: true, message: t('schedule.statusRequired') }]}
                      >
                        <Select>
                          <Option value="active">{t('common.active')}</Option>
                          <Option value="inactive">{t('common.inactive')}</Option>
                          <Option value="draft">{t('common.draft')}</Option>
                          <Option value="expired">{t('common.expired')}</Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name="priority"
                        label={t('schedule.priority')}
                        rules={[{ required: true, message: t('schedule.priorityRequired') }]}
                      >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>

                      <Form.Item
                        name="from_date"
                        label={t('schedule.fromDate')}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>

                      <Form.Item
                        name="to_date"
                        label={t('schedule.toDate')}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>

                      <Form.Item
                        name="timezone"
                        label={t('schedule.timezone')}
                      >
                        <Select defaultValue="Asia/Hong_Kong">
                          <Option value="Asia/Hong_Kong">Asia/Hong_Kong</Option>
                          <Option value="UTC">UTC</Option>
                        </Select>
                      </Form.Item>

                      <Divider style={{ margin: '12px 0' }} />

                      <Form.Item
                        name="is_available_for_telemedicine"
                        label={t('schedule.availableForTelemedicine')}
                        valuePropName="checked"
                      >
                        <Switch 
                          checkedChildren={<MedicineBoxOutlined />} 
                          unCheckedChildren={<StopOutlined />}
                        />
                      </Form.Item>

                      <Form.Item
                        name="is_available_for_walk_in"
                        label={t('schedule.availableForWalkIn')}
                        valuePropName="checked"
                      >
                        <Switch 
                          checkedChildren={<EnvironmentOutlined />} 
                          unCheckedChildren={<StopOutlined />}
                        />
                      </Form.Item>
                    </Space>
                  </Card>
                </Col>

                {/* Working Hours */}
                <Col xs={24} lg={16}>
                  <Card size="small" title={t('schedule.workingHours')}>
                    <Form.Item name="schedule" label={t('schedule.weeklySchedule')}>
                      {weekDays.map(({ key, label, icon }) => (
                        <div key={key} style={{ marginBottom: 16 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Text strong>{icon} {label}</Text>
                            <Button 
                              size="small" 
                              icon={<PlusOutlined />}
                              onClick={() => addTimeSlot(key)}
                              title={t('schedule.addTimeSlot')}
                            />
                          </div>
                          
                          <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.schedule !== currentValues.schedule}
                          >
                            {({ getFieldValue }) => {
                              const schedule = getFieldValue('schedule') || {};
                              const daySchedule = schedule[key] || [];
                              
                              return (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                  {daySchedule.map((timeSlot: TimeSlot, index: number) => (
                                    <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                      <Input
                                        type="time"
                                        value={timeSlot.from}
                                        onChange={(e) => updateTimeSlot(key, index, 'from', e.target.value)}
                                        style={{ width: 120 }}
                                      />
                                      <Text>-</Text>
                                      <Input
                                        type="time"
                                        value={timeSlot.to}
                                        onChange={(e) => updateTimeSlot(key, index, 'to', e.target.value)}
                                        style={{ width: 120 }}
                                      />
                                      <Button
                                        size="small"
                                        danger
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => removeTimeSlot(key, index)}
                                        title={t('schedule.removeTimeSlot')}
                                      />
                                    </div>
                                  ))}
                                  {daySchedule.length === 0 && (
                                    <Text type="secondary">{t('schedule.noTimeSlots')}</Text>
                                  )}
                                </Space>
                              );
                            }}
                          </Form.Item>
                        </div>
                      ))}
                    </Form.Item>
                  </Card>
                </Col>
              </Row>

              <Divider />

              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={onClose} disabled={isSubmitting}>
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={isSubmitting}
                    icon={<SaveOutlined />}
                  >
                    {editingSchedule ? t('schedule.updateSchedule') : t('schedule.createSchedule')}
                  </Button>
                </Space>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default ProviderScheduleConfig;
